import type { ComponentProps, ReactNode } from 'react';

import { Label } from '@/components/shadcn/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/tabs';

import { useFieldContext } from './form';
import { getFieldError } from './get-field-error';

export interface FormTabsOption {
  value: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
}

interface FormTabsProps extends Omit<
  ComponentProps<typeof Tabs>,
  'value' | 'onValueChange' | 'defaultValue' | 'children'
> {
  label?: string;
  labelClassName?: string;
  options: FormTabsOption[];
  listClassName?: string;
  triggerClassName?: string;
  showErrorMessage?: boolean;
}

export function FormTabs({
  label,
  labelClassName,
  options,
  listClassName,
  triggerClassName,
  showErrorMessage = true,
  ...props
}: FormTabsProps) {
  const field = useFieldContext<string>();

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <Label htmlFor={field.name} className={labelClassName}>
          {label}
        </Label>
      )}

      <Tabs
        value={field.state.value ?? ''}
        onValueChange={(value) => {
          if (value) {
            field.handleChange(value);
          }
        }}
        {...props}
      >
        <TabsList className={listClassName}>
          {options.map((option) => (
            <TabsTrigger
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={triggerClassName}
            >
              {option.icon}
              {option.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {showErrorMessage && field.state.meta.errors.length > 0 && (
        <span className="text-destructive text-sm">
          {getFieldError(field.state.meta.errors[0])}
        </span>
      )}
    </div>
  );
}
